import BONESMidwayJavaScriptClient from 'BONESMidwayJavaScriptClient';
import { deepClone, extractErrorMessage } from '../utilities';
import { deleteFromLocalStorage, loadFromLocalStorage, LOCAL_STORAGE_KEYS, saveToLocalStorage } from '../utils/local_storage';
import logger from '../utils/logging';
import { 
  SET_HISTOGRAM_DATA, 
  SET_SUMMARY_DATA_FA, 
  SET_SUMMARY_DATA_LA, 
  API_ERROR, 
  SET_TREND_DATA, 
  SET_ASIN_HISTORY_LA, 
  SET_ASIN_HISTORY_FA, 
  SET_PCOGS_SOURCE_FA, 
  SET_PCOGS_SOURCE_LA,
  SET_ASIN_DATA 
} from '../components/spotcp/actions/actionTypes';
import { prepareBridgingParams } from './reports/bridging/bridging_utils';

let client;

export function getJSClient() {
  if (!client) {
    client = new JSClient();
  }
  return client;
}

export function createJSClient(onReadyHandler) {
  if (!client) {
    client = new JSClient({ onReady: onReadyHandler });
  }
  return client;
}

export class JSClient {
  constructor(props) {
    this.props = props || {};
    new BONESMidwayJavaScriptClient({ onReady: this.clientLoaded, onErr: this.onErr });
  }

  onErr = (err) => {
    logger.error(`Error initialising BONESMidwayJavaScriptClient`, err);
  };

  clientLoaded = (client) => {
    this.client = client;

    if (this.props.hasOwnProperty('onReady')) {
      this.props.onReady();
    }
  };

  cachedPost = (endpoint, cacheKey, callback, errorCallback, payload) => {
    logger.info(`Posting for ${cacheKey}`);
    const cachedResponse = loadFromLocalStorage(cacheKey);
    if (cachedResponse && callback) {
      try {
        callback(cachedResponse);
      } catch (error) {
        logger.error(`Faced error while processing cached data of : ${cacheKey}`, error);
        deleteFromLocalStorage(cacheKey);
      }
    }
    this.client
      .post(endpoint, payload)
      .then((response) => {
        logger.info(`Received response from ${endpoint}`, response.data);
        callback(response.data);
        saveToLocalStorage(cacheKey, response.data);
      })
      .catch((error) => {
        logger.info(`Error while invoking API ${endpoint}`, error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  cachedGet = (endpoint, cacheKey, callback, errorCallback) => {
    logger.info(`Fetching from ${endpoint}`);
    let cachedResponse;
    if (cacheKey) {
      cachedResponse = loadFromLocalStorage(cacheKey);
    }
    if (cachedResponse && callback) {
      try {
        callback(cachedResponse);
      } catch (error) {
        console.error(`Faced error while processing cached data of : ${cacheKey}`, {}, error);
        deleteFromLocalStorage(cacheKey);
      }
    }
    this.client
      .get(endpoint)
      .then((response) => {
        logger.info(`Received response from ${endpoint}`, response.data);
        callback(response.data);
        if (cacheKey) {
          saveToLocalStorage(cacheKey, response.data);
        }
      })
      .catch((error) => {
        logger.error(`Error while invoking API ${ endpoint }`, error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  pullAccuracyData = (callback, errorCallback, payload, queryExecutionId, snapshots, index, item, retry) => {
    if (snapshots.length === 1) {
      logger.info(`Attempting to pull accuracy data from ${snapshots[0].id} for Dataset ${index + 1}`);
    } else {
      logger.info(`Attempting to pull accuracy data from ${snapshots[index].id} for Dataset ${index + 1}`);
    }
    let params = deepClone(payload);
    params.queryExecutionId = queryExecutionId;
    if (snapshots.length === 1) {
      if ('live' === snapshots[0].id) {
        params.snapshot = null;
      } else {
        params.snapshot = snapshots[0];
      }
    } else {
      if (!snapshots || !snapshots[index] || !snapshots[index].id) {
        logger.info('No snapshot found. Defaulting to live.');
        params.snapshot = null;
      } else if ('live' === snapshots[index].id) {
        params.snapshot = null;
      } else {
        params.snapshot = snapshots[index];
      }
    }
    this.client
      .post('accuracy', params)
      .then((response) => {
        callback(response.data, index, params, item, retry);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error, retry);
        }
      });
  };

  getMappings = (callback, errorCallback, payload) => {
    logger.info('Attempting to get sub component mappings');
    this.client
      .post('mappings', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  stageMappings = (callback, errorCallback, payload) => {
    logger.info('Attempting to stage mapping changes');
    this.client
      .post(`mappings/stage`, payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  unstageMappings = (callback, errorCallback, payload) => {
    logger.info('Attempting to unstage mapping changes');
    this.client
      .post(`mappings/unstage`, payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  publishMappings = (callback, errorCallback, payload) => {
    logger.info('Attempting to publish mapping changes');
    this.client
      .post(`mappings/publish`, payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getMappingImpact = (callback, errorCallback, payload) => {
    logger.info('Attempting to get impact of mappings');
    this.client
      .post('mappings/impact', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  downloadAccuracyReport = (callback, errorCallback, payload, queryExecutionId) => {
    logger.info('Attempting to download accuracy report.');
    payload.queryExecutionId = queryExecutionId;
    this.client
      .post('accuracy/download', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getBridgingFactorsReport = (callback, page, errorCallback, payload, type = "") => {
    logger.info('Attempting to get bridging factors');
    let params = prepareBridgingParams(payload);

    if (page) {
      params = `${params}&page=${page}`
    }

    this.client
        .get(`bridgingReport?${params}`, {})
        .then((response) => {
          callback(response.data, page, payload, type);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  getBridgingFactorsAgreementsReport = (callback, errorCallback, payload) => {
    logger.info('Attempting to get bridging factors');
    let params = prepareBridgingParams(payload);

    this.client
      .get(`bridgingReportAgreement?${params}`, {})
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  downloadBridgingAgreementReport = (callback, errorCallback, payload) => {
    logger.info('Attempting to download Bridging Agreement report.');
    let params = prepareBridgingParams(payload);

    this.client
      .get(`bridgingReportAgreement/download?${params}`, {})
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  downloadAccuracyBenchmark = (callback, errorCallback, payload) => {
    logger.info('Attempting to download component benchmark.');
    this.client
      .post('benchmark/download', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  downloadWWReport = (callback, errorCallback, payload) => {
    logger.info('Attempting to download ww wbr report.');
    this.client
      .post('ww/download', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  downloadAsinWWWBRReport = (callback, errorCallback, payload) => {
    logger.info('Attempting to download asin ww wbr report.');
    this.client
        .post('ww/asin/download', payload)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  downloadHCPSpotReport = (callback, errorCallback, payload) => {
    logger.info('Attempting to download HCP-Spot report.');
    this.client
      .post('hcpSpot/download', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getIdentity = (callback, errorCallback) => {
    this.cachedGet('identity', LOCAL_STORAGE_KEYS.IDENTITY, callback, errorCallback);
  };

  getFeatureAccessItems = (callback, errorCallback) => {
    this.cachedGet('featureAccess', LOCAL_STORAGE_KEYS.FEATURE_ACCESS, callback, errorCallback);
  };

  getConfig = (callback, errorCallback, queryParams) => {
    const endpoint = queryParams ? `config?${queryParams}` : 'config';
    this.cachedGet(endpoint, LOCAL_STORAGE_KEYS.CONFIG, callback, errorCallback);
  };

  getComponentConfig = (callback, errorCallback, includeSubComponents) => {
    this.cachedGet(`config?type=component&includeSubComponents=${includeSubComponents}`, LOCAL_STORAGE_KEYS.COMPONENT_CONFIG, callback, errorCallback);
  };

  getMarketplaceConfig = (callback, errorCallback) => {
    this.cachedGet('config?type=marketplace', LOCAL_STORAGE_KEYS.MARKETPLACE_CONFIG, callback, errorCallback);
  };

  getChannelTrendGoalConfig = (callback, errorCallback) => {
      this.cachedGet('config?type=channelTrendGoal', LOCAL_STORAGE_KEYS.CHANNEL_TREND_GOAL_CONFIG, callback, errorCallback);
  };

  getComponentTrendGoalConfig = (callback, errorCallback) => {
    this.cachedGet('config?type=componentTrendGoal', LOCAL_STORAGE_KEYS.COMPONENT_TREND_GOAL_CONFIG, callback, errorCallback);
  };

  pullSavedReports = (callback, errorCallback, payload) => {
    this.cachedPost('reports', LOCAL_STORAGE_KEYS.SAVED_REPORTS, callback, errorCallback, payload);
  };

  pullSnapshots = (callback, errorCallback, payload) => {
    logger.info('Attempting to pull snapshots');
    this.cachedPost('config/snapshot', LOCAL_STORAGE_KEYS.SNAPSHOTS, callback, errorCallback, payload);
  };

  pullColumnConfig = (callback, errorCallback) => {
    logger.info('Attempting to pull column config');
    this.cachedGet('columns', LOCAL_STORAGE_KEYS.COLUMN_CONFIG, callback, errorCallback);
  };

  pullOPPUHierarchy = (callback, errorCallback) => {
    logger.info('Attempting to pull OPPU hierarchy');
    this.cachedGet('hierarchy', LOCAL_STORAGE_KEYS.HIERARCHY_CONFIG, callback, errorCallback);
  };

  getDashboardUrl = (callback, errorCallback, payload) => {
    logger.info(`Attempting to get dashboard url.`, {payload});
    this.client
      .post('dashboard', payload)
      .then((response) => callback(response.data))
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getSpotCP = (callback, errorCallback, payload) => {
    logger.info(`Attempting to get Spot CP response.`, {payload});
    this.client
      .post('spot', payload)
      .then((response) => callback(response.data))
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  saveCognosConfigChanges = (callback, errorCallback, payload) => {
    this.client
      .post('config/cognos', payload)
      .then((response) => callback(response.data))
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getAppConfig = (callback, errorCallback) => {
    logger.info('Attempting to get App config');
    let endpoint = 'config/app';
    this.cachedGet(endpoint, endpoint, callback, errorCallback);
  };

  getPlHierarchy = (callback, errorCallback, payload) => {
    logger.info('Attempting to get PL Hierarchy');
    const id = payload.id || 'ALL';
    const version = payload.version || '0';
    const includeHierarchy = payload.includeHierarchy || true;
    const endpoint = `productLine?id=${id}&version=${version}&includeHierarchy=${includeHierarchy}`;
    if (id === 'ALL') {
      this.cachedGet(endpoint, LOCAL_STORAGE_KEYS.PRODUCT_LINES + endpoint, callback, errorCallback);
    } else {
      this.cachedGet(endpoint, undefined, callback, errorCallback);
    }
  };

  stageProductLine = (callback, errorCallback, payload) => {
    logger.info('Attempting to stage productLine changes');
    this.client
      .post(`productLine/stage`, payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  unstageProductLine = (callback, errorCallback, payload) => {
    logger.info('Attempting to unstage productLine changes');
    this.client
      .post(`productLine/unstage`, payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  publishProductLineMappings = (callback, errorCallback, payload) => {
    logger.info('Attempting to publish productLine changes');
    this.client
      .post(`productLine/publish`, payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getGLProductGroups = (callback, errorCallback) => {
    logger.info('Attempting to get GL Product Groups');
    this.cachedGet('glProductGroup', LOCAL_STORAGE_KEYS.GL_PRODUCT_GROUPS, callback, errorCallback);
  };

  saveAppConfig = (callback, errorCallback, payload) => {
    logger.info('Attempting to save App config');
    this.client
      .post('config/app', payload)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  saveReport = (callback, errorCallback, payload) => {
    logger.info(`Attempting to save report ${payload.reportToSave.id}`);
    this.client
      .post('reports/save', payload)
      .then((response) => {
        callback(payload.reportToSave.id);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getSimulations = (callback, errorCallback, payload) => {
    let endpoint = `simulations`;
    if (payload.user && payload.id) {
      endpoint = `simulations?user=${payload.user}&id=${payload.id}`;
    } else if (payload.user) {
      endpoint = `simulations?user=${payload.user}`;
    } else if (payload.id) {
      endpoint = `simulations?id=${payload.id}`;
    }
    this.cachedGet(endpoint, endpoint, callback, errorCallback);
  };

  runSimulation = (callback, errorCallback, payload) => {
    logger.info(`Attempting to run simulation ${payload.name}`);
    this.client
      .post('simulations/run', payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  updateSimulation = (callback, errorCallback, payload) => {
    logger.info(`Attempting to update simulation ${payload.simulation.name}`);
    this.client
      .put('simulations', payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getValidationTestResults = (callback, errorCallback, payload) => {
    let endpoint = `validations/testresults?snapshotId=${payload.snapshotId}`;

    if (payload.executionId) {
      // Sending the character '#' in the URL isn't recommended and browser might remove everything after '#'
      // API GW also responds '403'. Hence doing a string replace. Backend will replace it back before doing
      // a lookup in datasource (DDB).
      endpoint = `${endpoint}&executionId=${payload.executionId.replace('#', 'HASH')}`;
    }

    if (payload.executionId && payload.prefixLookup !== null && payload.prefixLookup !== undefined) {
      endpoint = `${endpoint}&prefixLookup=${payload.prefixLookup}`;
    }

    this.cachedGet(endpoint, endpoint, callback, errorCallback);
  };

  deleteSnapshot = (callback, errorCallback, payload) => {
    const snapshotId = payload.id;
    logger.info(`Attempting to delete snapshot ${snapshotId}`);
    const endpoint = `/config/snapshot?id=${snapshotId}`
    this.client
      .delete(endpoint)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  saveSnapshot = (callback, errorCallback, payload) => {
    logger.info(`Attempting to save snapshot ${payload.config.id}`);
    this.client
      .post('config/snapshot/save', payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  promoteSnapshot = (callback, errorCallback, payload) => {
    logger.info(`Attempting to promote snapshot ${payload.id}`);
    this.client
      .post('config/snapshot/promote', payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  pullUserPreferences = (callback, errorCallback, user) => {
    logger.info('Attempting to get user preferences');
    let endpoint = '/preferences?user=' + user;
    this.cachedGet(endpoint, endpoint, callback, errorCallback);
  };

  saveUserPreferences = (callback, errorCallback, payload, user) => {
    logger.info('Attempting to save user preferences');
    let endpoint = '/preferences/save';
    saveToLocalStorage('/preferences?user=' + user, payload);
    this.client
      .post(endpoint, payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback();
        }
      });
  };

  getNewFeatureDisplayItems = (callback, errorCallback) => {
    logger.info('Attempting to get new feature display items');
    this.client
      .get('newfeaturedisplay')
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  runValidations = (callback, errorCallback, payload) => {
    logger.info(`Attempting to run validations`);
    this.client
      .post('validations/run', payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        logger.error(extractErrorMessage(error), error);
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  getAccuracyDrivers = (callback, errorCallback, payload) => {
    logger.info(`Attempting to get accuracy drivers`);
    this.client
        .post('accuracy/drivers', payload)
        .then((response) => {
          callback(response);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  runSpotcpAsinHistogram = (dispatch, payload) => {
    logger.info('Attempting to get spotcp asin histogram');
    const api = 'spotcp/histogram';
    this.client
      .post(api, payload)
      .then((response) => dispatch({ type: SET_HISTOGRAM_DATA, payload: response.data.resultData }))
      .catch((error) => {
        dispatch({ type: API_ERROR, payload: {api, error} })
      });
  };

  runSpotcpAsinHistogramFaAndLa = (dispatch, payload) => {
    this.runSpotcpAsinHistogram(dispatch, payload) // FA
    this.runSpotcpAsinHistogram(dispatch, {...payload, useLA: true}) // LA
  };

  runSpotcpSummary = (dispatch, payload) => {
    logger.info('Attempting to get spotcp summary');
    const api = 'spotcp/summary';
    let actionType = payload.useLA ? SET_SUMMARY_DATA_LA : SET_SUMMARY_DATA_FA;
    this.client
      .post(api, payload)
      .then((response) => dispatch({ type: actionType, payload: response.data.resultData }))
      .catch((error) => {
        dispatch({ type: API_ERROR, payload: {api, error} })
      });

  };

  runSpotcpAdvancedFilter = (dispatch, payload) => {
    logger.info('Attempting to get spotcp advanced filter resport');
    const api = 'spotcp/advanced';
    let actionType;
    const {useLA, filter} = payload;
    switch (filter) {
      case "ASIN_HISTORY":
        actionType = useLA ? SET_ASIN_HISTORY_LA : SET_ASIN_HISTORY_FA;
        break;
      case "PCOGS_SOURCE":
        actionType = useLA ? SET_PCOGS_SOURCE_LA : SET_PCOGS_SOURCE_FA; 
        break;
      default:
        logger.error(`Unsupported Spot CP Advanced filter: ${filter}`);
    }
    this.client
      .post(api, payload)
      .then((response) => dispatch({ type: actionType, payload: response.data.resultData }))
      .catch((error) => {
        dispatch({ type: API_ERROR, payload: {api, error} })
      });
  }

  runSpotcpTrend = (dispatch, payload) => {
    logger.info('Attempting to get spotcp trend');
    const api = 'spotcp/trend';
    this.client
      .post(api, payload)
      .then((response) => dispatch({ type: SET_TREND_DATA, payload: response.data.resultData }))
      .catch((error) => {
        dispatch({ type: API_ERROR, payload: {api, error} })
      });
  };

  runSpotcpTrendFaAndLa = (dispatch, payload) => {
    this.runSpotcpTrend(dispatch, payload); // FA
    this.runSpotcpTrend(dispatch, {...payload, useLA: true}) // LA
  }

  runAsinAnalysis = (dispatch, payload) => {
    logger.info('Attempting to get spotcp asin analysis');
    const api = 'spotcp/asin';
    this.client
      .post(api, payload)
      .then((response) => dispatch({ type: SET_ASIN_DATA, payload: response.data }))
      .catch((error) => {
        dispatch({ type: API_ERROR, payload: {api, error} })
      });
  }

  getChannelMappings = (callback, errorCallback) => {
    logger.info('Attempting to get channel mappings');
    this.client
        .get('channelMapping')
        .then((response) => {
          callback(response);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  stageChannelMapping = (callback, errorCallback, payload) => {
    logger.info('Attempting to stage channelMapping changes');
    this.client
        .post(`channelMapping/stage`, payload)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  unstageChannelMapping = (callback, errorCallback, payload) => {
    logger.info('Attempting to unstage channelMapping changes');
    this.client
        .post(`channelMapping/unstage`, payload)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  publishChannelMapping = (callback, errorCallback, payload) => {
    logger.info('Attempting to publish channelMapping changes');
    this.client
        .post(`channelMapping/publish`, payload)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  addChannelMapping = (callback, errorCallback, payload) => {
    logger.info('Attempting to add channelMapping');
    this.client
        .post(`channelMapping/add`, payload)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };

  getPresignedS3Url = (callback, errorCallback, payload) => {
    logger.info('Attempting to get presigned s3 url for adjustment file upload');
    const endpoint = `adjustment/upload?username=${payload.username}&fileType=${payload.fileType}`
    this.client
        .get(endpoint)
        .then((response) => callback(response.data))
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if(errorCallback) {
            errorCallback(error);
          }
        });
  };

  saveBenchmarkAdjustmentDraft = (callback, errorCallback, payload) => {
    logger.info('Attempting to save uploaded file to draft in DynamoDB');
    this.client
        .post(`adjustment/draft`, payload)
        .then((response) => callback(response.data))
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if(errorCallback) {
            errorCallback(error);
          }
        });
  };

  getBenchmarkAdjustmentDraft = (callback, errorCallback, payload) => {
    logger.info('Attempting to get benchmark adjustment draft');
    const endpoint = `adjustment/draft?username=${payload.username}`
    this.client
        .get(endpoint)
        .then((response) => callback(response.data))
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if(errorCallback) {
            errorCallback(error);
          }
        });
  };

  discardBenchmarkAdjustmentDraft = (callback, errorCallback, payload) => {
    logger.info('Attempting to discard benchmark adjustment draft');
    const endpoint = `adjustment/draft?username=${payload.username}`
    this.client
        .delete(endpoint)
        .then((response) => callback(response.data))
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if(errorCallback) {
            errorCallback(error);
          }
        });
  };

  publishBenchmarkAdjustment = (callback, errorCallback, payload) => {
    logger.info('Attempting to publish benchmark adjustment');
    this.client
        .post(`adjustment`, payload)
        .then((response) => callback(response.data))
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if(errorCallback) {
            errorCallback(error);
          }
        });
  };

  getFLCPAccuracySummary = (callback, errorCallback, payload) => {
    logger.info('Attempting to get flcp accuracy summary');
    const endpoint = `accuracy/flcp/summary?timeFrame=${ payload.timeFrame }&channel=${ payload.channel }`
      + `&accuracyType=${ payload.accuracyType }&gls=${ payload.gls }`
      + `&rollup=${ payload.rollup }&region=${ payload.region }&marketplace=${ payload.marketplace }&day=${payload.day}`;

    this.client
        .get(endpoint, payload)
        .then((response) => callback(response.data))
        .catch((error) => {
          logger.error(extractErrorMessage(error), error);
          if (errorCallback) {
            errorCallback(error);
          }
        });
  };
}



// WEBPACK FOOTER //
// ./src/components/js_client.jsx